import React, { useEffect } from "react";
import { colors } from "../../utils/Colors";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { useLocation, useParams } from "react-router-dom";
import { buildBreadcrumbLD, buildCanonical, buildTitle, jsonLdString, truncate } from "../../utils/seo";
import { useDispatch, useSelector } from "react-redux";
import { useAuthContext } from "../../features/authentication";
import { JobDetailStyles } from "../../components/Jobs/JobDetailStyled";
import { BsChevronRight } from "react-icons/bs";
import { getJobById } from "../../store/thunks";
import JobInfoSection, {
  JobInfoPlaceholder,
} from "../../components/Jobs/JobInfoSection";
import RectanglePlaceholder from "../../components/common/Placeholders/RectanglePlaceholder";
import JobOffersSection from "../../components/Jobs/JobOffersSection";
import ProjectInfoSection, { ProjectInfoPlaceholder } from "../../components/Projects/ProjectInfoSection";
import ProjectOffersSection from "../../components/Projects/ProjectOffersSection";
import CreateProjectOfferForm from "../../components/Jobs/CreateOfferForm";
import { Helmet } from "react-helmet-async";

/** @typedef {import('../../types/server').JobDetailData} JobDetailDataState */
const ProjectDetail = () => {
  const { state: initalData } = useLocation();
  const queryParams = useParams();
  const dispatch = useDispatch();
  const projectId = queryParams.projectId;
  const { data: jobData, loading: jobLoading } = useSelector(
    (state) => state.jobs.getJobById
  );

  const data = /** @type {import('../../types/server').JobDetailData} **/ (
    jobData || initalData
  );

  const { user } = useAuthContext();

  // Not going the use the data from the state because it is not updated, and sometime createBy._id is undefined
  const initalLoading = jobLoading;
  const isCurrentUserTheCreator =
    user && data && user._id === data.createdBy._id;

  useEffect(() => {
    dispatch(getJobById({ id: projectId }));
  }, [dispatch, projectId]);

  return (
    <>
      {data && (() => {
        const projectTitle = data.title || truncate(data.description, 60);
        const canonical = buildCanonical(`/projects/${projectId}/detail`);
        const description = truncate(data.description) || "View project details on GigFalcon.";
        const locationText = data.location?.address ? ` near ${data.location.address}` : "";
        return (
          <Helmet>
            <title>{buildTitle(`${projectTitle}${locationText}`)}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            <meta property="og:title" content={`${projectTitle}${locationText}`} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:type" content="website" />
            {data.attachments?.[0]?.imgUrl && <meta property="og:image" content={data.attachments[0].imgUrl} />}
            <meta name="twitter:card" content="summary_large_image" />
            {data.attachments?.[0]?.imgUrl && <meta name="twitter:image" content={data.attachments[0].imgUrl} />}
            <script type="application/ld+json">{jsonLdString(buildBreadcrumbLD([
              { name: "Home", url: "https://www.gigfalcon.com/" },
              { name: "Projects", url: "https://www.gigfalcon.com/" },
              { name: projectTitle, url: canonical },
            ]))}</script>
          </Helmet>
        );
      })()}
      <Header />
      <JobDetailStyles colors={colors} className="container">
        <div className="d-flex flex-column gap-2 mb-3">
          <div className="d-flex gap-2 align-items-center text-muted text-sm">
            {initalLoading ? (
              <>
                <p style={{ width: 80 }}>
                  <span className="placeholder placeholder-grey w-100"></span>
                </p>
                <RectanglePlaceholder size={12} className="mb-2" />
                <p style={{ width: 80 }}>
                  <span className="placeholder placeholder-grey w-100"></span>
                </p>
              </>
            ) : (
              <>
                <p className="text-capitalize">{data?.category?.name}</p>
                <BsChevronRight size={8} />
                <p className="text-capitalize">{data?.subcategory?.name}</p>
              </>
            )}
          </div>
          {initalLoading ? (
            <h2 className="placeholder-glow w-25" style={{ height: 24 }}>
              <span className="placeholder placeholder-grey w-100"></span>
            </h2>
          ) : (
            <h3 className="m-0">
              Project <span className="falcon-clr">Details</span>
            </h3>
          )}
        </div>
        {initalLoading ? <ProjectInfoPlaceholder /> : <ProjectInfoSection {...data} />}
        {initalLoading ? null : !user ? null : isCurrentUserTheCreator ? (
          <ProjectOffersSection projectId={data._id} />
        ) : (
          <CreateProjectOfferForm
            projectId={data?._id}
            offer={{ budgetMax: data?.budgetMax, budgetMin: data?.budgetMin }}
          />
        )}
      </JobDetailStyles>
      <Footer />
    </>
  );
};

export default ProjectDetail;
